import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { api, getAuthHeaders } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import './Reports.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function Reports({ tenantId }) {
  const { showNotification } = useNotification();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [fleet, setFleet] = useState([]);

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadSummary();
      loadRepairs();
      loadInvoices();
      loadCustomers();
      loadPurchases();
      loadInventory();
      loadFleet();
    }
  }, [tenantId, selectedYear]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadSummary = async () => {
    try {
      const data = await api.get(`/reports/summary/${selectedYear}`, tenantId);
      setSummary(data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadRepairs = async () => {
    try {
      const data = await api.get('/repairs', tenantId);
      setRepairs(data);
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await api.get('/invoices', tenantId);
      setInvoices(data);
      
      const last6Months = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthInvoices = data.filter(inv => {
          const invDate = new Date(inv.date);
          return `${invDate.getFullYear()}-${String(invDate.getMonth() + 1).padStart(2, '0')}` === monthKey;
        });
        const total = monthInvoices.reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
        last6Months.push({
          month: date.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' }),
          total
        });
      }
      setMonthlyData(last6Months);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadPurchases = async () => {
    try {
      const data = await api.get('/purchases', tenantId);
      setPurchases(data);
    } catch (error) {
      console.error('Error loading purchases:', error);
    }
  };

  const loadInventory = async () => {
    try {
      const data = await api.get('/inventory', tenantId);
      setInventory(data);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const loadFleet = async () => {
    try {
      const data = await api.get('/cars?fleetOnly=true', tenantId);
      setFleet(data);
    } catch (error) {
      console.error('Error loading fleet:', error);
    }
  };

  const handleDownloadPDF = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/reports/tax/${selectedYear}`, {
        headers: getAuthHeaders(tenantId)
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Steuerberater-${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showNotification('Fehler beim Herunterladen der PDF', 'error');
    } finally {
      setLoading(false);
    }
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= currentYear - 5; i--) {
    years.push(i);
  }

  return (
    <div className="reports">
      <div className="page-header">
        <h1>Steuerberater-Übersichten</h1>
      </div>

      <div className="reports-content">
        <div className="year-selector">
          <label>Jahr:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="year-select"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button
            onClick={handleDownloadPDF}
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Wird erstellt...' : 'PDF herunterladen'}
          </button>
        </div>

        <div className="info-box">
          <h3>Inhalt der PDF-Übersicht</h3>
          <ul>
            <li>Umsatzübersicht (netto, brutto, MwSt.)</li>
            <li>Ausgabenübersicht (Anschaffungen, Mietkosten)</li>
            <li>Gewinn/Verlust-Rechnung</li>
            <li>Detaillierte Rechnungsliste</li>
            <li>Detaillierte Anschaffungsliste</li>
            <li>Mietkosten-Übersicht</li>
          </ul>
        </div>

        {summary && (
          <div className="summary-grid">
            <div className="summary-card">
              <h3>Umsatz</h3>
              <div className="summary-item">
                <span>Brutto:</span>
                <strong>{summary.revenue.gross.toFixed(2)} {currency}</strong>
              </div>
              <div className="summary-item">
                <span>Netto:</span>
                <strong>{summary.revenue.net.toFixed(2)} {currency}</strong>
              </div>
              <div className="summary-item">
                <span>MwSt.:</span>
                <strong>{summary.revenue.tax.toFixed(2)} {currency}</strong>
              </div>
              <div className="summary-item">
                <span>Anzahl Rechnungen:</span>
                <strong>{summary.invoiceCount}</strong>
              </div>
            </div>

            <div className="summary-card">
              <h3>Ausgaben</h3>
              <div className="summary-item">
                <span>Anschaffungen:</span>
                <strong>{summary.expenses.purchases.toFixed(2)} {currency}</strong>
              </div>
              <div className="summary-item">
                <span>Mietkosten:</span>
                <strong>{summary.expenses.rent.toFixed(2)} {currency}</strong>
              </div>
              <div className="summary-item total">
                <span>Gesamt:</span>
                <strong>{summary.expenses.total.toFixed(2)} {currency}</strong>
              </div>
              <div className="summary-item">
                <span>Anzahl Anschaffungen:</span>
                <strong>{summary.purchaseCount}</strong>
              </div>
            </div>

            <div className="summary-card highlight">
              <h3>Ergebnis</h3>
              <div className="summary-item large">
                <span>Gewinn/Verlust:</span>
                <strong className={summary.profit >= 0 ? 'positive' : 'negative'}>
                  {summary.profit >= 0 ? '+' : ''}{summary.profit.toFixed(2)} {currency}
                </strong>
              </div>
            </div>
          </div>
        )}

        <div className="reports-sections">
          <section className="report-section">
            <h2 className="section-title">Umsatz-KPIs</h2>
            <div className="kpi-grid">
              <div className="kpi-card">
                <div className="kpi-label">Durchschnittlicher Monatsumsatz</div>
                <div className="kpi-value">
                  {monthlyData.length > 0 
                    ? (monthlyData.reduce((sum, d) => sum + d.total, 0) / monthlyData.length).toFixed(2)
                    : '0.00'
                  } {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Höchster Monatsumsatz</div>
                <div className="kpi-value">
                  {monthlyData.length > 0
                    ? Math.max(...monthlyData.map(d => d.total)).toFixed(2)
                    : '0.00'
                  } {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Gesamtumsatz (6 Monate)</div>
                <div className="kpi-value">
                  {monthlyData.reduce((sum, d) => sum + d.total, 0).toFixed(2)} {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Anzahl Kunden</div>
                <div className="kpi-value">{customers.length}</div>
              </div>
            </div>
            {monthlyData.length > 0 && (
              <div className="charts-grid">
                <div className="chart-card">
                  <h3>Monatlicher Umsatz (letzte 6 Monate)</h3>
                  <div style={{ height: '300px' }}>
                    <Line
                      data={{
                        labels: monthlyData.map(d => d.month),
                        datasets: [{
                          label: 'Umsatz',
                          data: monthlyData.map(d => d.total),
                          borderColor: '#2196f3',
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          tension: 0.4
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            display: false
                          },
                          tooltip: {
                            callbacks: {
                              label: (context) => {
                                return `${context.parsed.y.toFixed(2)} ${currency}`;
                              }
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: (value) => {
                                return `${value.toFixed(0)} ${currency}`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="chart-card">
                  <h3>Umsatz nach Monaten</h3>
                  <div style={{ height: '300px' }}>
                    <Bar
                      data={{
                        labels: monthlyData.map(d => d.month),
                        datasets: [{
                          label: 'Umsatz',
                          data: monthlyData.map(d => d.total),
                          backgroundColor: 'rgba(33, 150, 243, 0.6)',
                          borderColor: '#2196f3',
                          borderWidth: 1
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            display: false
                          },
                          tooltip: {
                            callbacks: {
                              label: (context) => {
                                return `${context.parsed.y.toFixed(2)} ${currency}`;
                              }
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: (value) => {
                                return `${value.toFixed(0)} ${currency}`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className="report-section">
            <h2 className="section-title">Reparatur-KPIs</h2>
            <div className="kpi-grid">
              {(() => {
                const repairStatusMapping = {
                  'pending': 'offen',
                  'in_progress': 'in_arbeit',
                  'completed': 'abgeschlossen',
                  'cancelled': 'storniert',
                  'offen': 'offen',
                  'in_arbeit': 'in_arbeit',
                  'abgeschlossen': 'abgeschlossen',
                  'storniert': 'storniert'
                };

                const getRepairMappedStatus = (status) => {
                  return repairStatusMapping[status] || 'offen';
                };

                const openRepairs = repairs.filter(r => {
                  const mappedStatus = getRepairMappedStatus(r.status || 'pending');
                  return mappedStatus === 'offen';
                });

                const inProgressRepairs = repairs.filter(r => {
                  const mappedStatus = getRepairMappedStatus(r.status || 'pending');
                  return mappedStatus === 'in_arbeit';
                });

                const completedRepairs = repairs.filter(r => {
                  const mappedStatus = getRepairMappedStatus(r.status || 'pending');
                  return mappedStatus === 'abgeschlossen';
                });

                return (
                  <>
                    <div className="kpi-card">
                      <div className="kpi-label">Gesamt Reparaturen</div>
                      <div className="kpi-value">{repairs.length}</div>
                    </div>
                    <div className="kpi-card">
                      <div className="kpi-label">Offene Reparaturen</div>
                      <div className="kpi-value">{openRepairs.length}</div>
                    </div>
                    <div className="kpi-card">
                      <div className="kpi-label">In Arbeit</div>
                      <div className="kpi-value">{inProgressRepairs.length}</div>
                    </div>
                    <div className="kpi-card">
                      <div className="kpi-label">Abgeschlossen</div>
                      <div className="kpi-value">{completedRepairs.length}</div>
                    </div>
                  </>
                );
              })()}
            </div>
            {repairs.length > 0 && (() => {
              const statusMapping = {
                'pending': 'offen',
                'in_progress': 'in_arbeit',
                'completed': 'abgeschlossen',
                'cancelled': 'storniert',
                'offen': 'offen',
                'in_arbeit': 'in_arbeit',
                'abgeschlossen': 'abgeschlossen',
                'storniert': 'storniert'
              };

              const statusCounts = repairs.reduce((acc, repair) => {
                const rawStatus = repair.status || 'pending';
                const mappedStatus = statusMapping[rawStatus] || 'offen';
                acc[mappedStatus] = (acc[mappedStatus] || 0) + 1;
                return acc;
              }, {});
              
              const statusLabels = {
                'offen': 'Offen',
                'in_arbeit': 'In Arbeit',
                'abgeschlossen': 'Abgeschlossen',
                'storniert': 'Storniert'
              };

              const colors = {
                'offen': 'rgba(33, 150, 243, 0.8)',
                'in_arbeit': 'rgba(255, 152, 0, 0.8)',
                'abgeschlossen': 'rgba(76, 175, 80, 0.8)',
                'storniert': 'rgba(244, 67, 54, 0.8)'
              };

              const statusOrder = ['offen', 'in_arbeit', 'abgeschlossen', 'storniert'];
              let orderedEntries = statusOrder
                .filter(key => statusCounts[key] && statusCounts[key] > 0)
                .map(key => [key, statusCounts[key]]);
              
              if (orderedEntries.length === 0) {
                orderedEntries = Object.entries(statusCounts)
                  .filter(([, value]) => value > 0)
                  .map(([key]) => [key, statusCounts[key]]);
              }

              const total = Object.values(statusCounts).reduce((sum, val) => sum + val, 0);

              if (orderedEntries.length === 0) {
                return null;
              }

              return (
                <div className="chart-card chart-card-pie">
                  <h3>Reparaturen nach Status</h3>
                  <div className="pie-chart-container">
                    <div className="pie-chart-wrapper">
                      <div style={{ width: '120px', height: '120px' }}>
                        <Doughnut
                          data={{
                            labels: orderedEntries.map(([key]) => statusLabels[key] || key),
                            datasets: [{
                              data: orderedEntries.map(([, value]) => value),
                              backgroundColor: orderedEntries.map(([key]) => colors[key] || '#999'),
                              borderWidth: 2,
                              borderColor: '#fff'
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                              legend: {
                                display: false
                              },
                              tooltip: {
                                enabled: true,
                                callbacks: {
                                  label: (context) => {
                                    const label = context.label || '';
                                    const value = context.parsed || 0;
                                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                    return `${label}: ${value} (${percentage}%)`;
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="pie-chart-legend">
                      {orderedEntries.map(([key, value]) => {
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={key} className="pie-legend-item">
                            <div className="pie-legend-color" style={{ backgroundColor: colors[key] || '#999' }}></div>
                            <div className="pie-legend-text">
                              <div className="pie-legend-label">{statusLabels[key] || key}</div>
                              <div className="pie-legend-value">{value} ({percentage}%)</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
          </section>

          <section className="report-section">
            <h2 className="section-title">Rechnungs-KPIs</h2>
            <div className="kpi-grid">
              {(() => {
                const invoiceStatusMapping = {
                  'pending': 'offen',
                  'sent': 'offen',
                  'paid': 'bezahlt',
                  'overdue': 'überfällig',
                  'cancelled': 'storniert',
                  'offen': 'offen',
                  'bezahlt': 'bezahlt',
                  'überfällig': 'überfällig',
                  'storniert': 'storniert'
                };

                const getInvoiceMappedStatus = (status) => {
                  return invoiceStatusMapping[status] || 'offen';
                };

                const openInvoices = invoices.filter(inv => {
                  const mappedStatus = getInvoiceMappedStatus(inv.status || 'pending');
                  return mappedStatus === 'offen';
                });

                const paidInvoices = invoices.filter(inv => {
                  const mappedStatus = getInvoiceMappedStatus(inv.status || 'pending');
                  return mappedStatus === 'bezahlt';
                });

                const overdueInvoices = invoices.filter(inv => {
                  const mappedStatus = getInvoiceMappedStatus(inv.status || 'pending');
                  return mappedStatus === 'überfällig';
                });

                return (
                  <>
                    <div className="kpi-card">
                      <div className="kpi-label">Gesamt Rechnungen</div>
                      <div className="kpi-value">{invoices.length}</div>
                    </div>
                    <div className="kpi-card">
                      <div className="kpi-label">Offene Rechnungen</div>
                      <div className="kpi-value">{openInvoices.length}</div>
                    </div>
                    <div className="kpi-card">
                      <div className="kpi-label">Bezahlte Rechnungen</div>
                      <div className="kpi-value">{paidInvoices.length}</div>
                    </div>
                    <div className="kpi-card">
                      <div className="kpi-label">Überfällige Rechnungen</div>
                      <div className="kpi-value">{overdueInvoices.length}</div>
                    </div>
                  </>
                );
              })()}
            </div>
            {invoices.length > 0 && (() => {
              const statusMapping = {
                'pending': 'offen',
                'sent': 'offen',
                'paid': 'bezahlt',
                'overdue': 'überfällig',
                'cancelled': 'storniert',
                'offen': 'offen',
                'bezahlt': 'bezahlt',
                'überfällig': 'überfällig',
                'storniert': 'storniert'
              };

              const statusCounts = invoices.reduce((acc, invoice) => {
                const rawStatus = invoice.status || 'pending';
                const mappedStatus = statusMapping[rawStatus] || 'offen';
                acc[mappedStatus] = (acc[mappedStatus] || 0) + 1;
                return acc;
              }, {});
              
              const statusLabels = {
                'offen': 'Offen',
                'bezahlt': 'Bezahlt',
                'überfällig': 'Überfällig',
                'storniert': 'Storniert'
              };

              const colors = {
                'offen': 'rgba(33, 150, 243, 0.8)',
                'bezahlt': 'rgba(76, 175, 80, 0.8)',
                'überfällig': 'rgba(255, 152, 0, 0.8)',
                'storniert': 'rgba(244, 67, 54, 0.8)'
              };

              const statusOrder = ['offen', 'bezahlt', 'überfällig', 'storniert'];
              let orderedEntries = statusOrder
                .filter(key => statusCounts[key] && statusCounts[key] > 0)
                .map(key => [key, statusCounts[key]]);
              
              if (orderedEntries.length === 0) {
                orderedEntries = Object.entries(statusCounts)
                  .filter(([, value]) => value > 0)
                  .map(([key]) => [key, statusCounts[key]]);
              }

              const total = Object.values(statusCounts).reduce((sum, val) => sum + val, 0);

              if (orderedEntries.length === 0) {
                return null;
              }

              return (
                <div className="chart-card chart-card-pie">
                  <h3>Rechnungen nach Status</h3>
                  <div className="pie-chart-container">
                    <div className="pie-chart-wrapper">
                      <div style={{ width: '120px', height: '120px' }}>
                        <Doughnut
                          data={{
                            labels: orderedEntries.map(([key]) => statusLabels[key] || key),
                            datasets: [{
                              data: orderedEntries.map(([, value]) => value),
                              backgroundColor: orderedEntries.map(([key]) => colors[key] || '#999'),
                              borderWidth: 2,
                              borderColor: '#fff'
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                              legend: {
                                display: false
                              },
                              tooltip: {
                                enabled: true,
                                callbacks: {
                                  label: (context) => {
                                    const label = context.label || '';
                                    const value = context.parsed || 0;
                                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                    return `${label}: ${value} (${percentage}%)`;
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="pie-chart-legend">
                      {orderedEntries.map(([key, value]) => {
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={key} className="pie-legend-item">
                            <div className="pie-legend-color" style={{ backgroundColor: colors[key] || '#999' }}></div>
                            <div className="pie-legend-text">
                              <div className="pie-legend-label">{statusLabels[key] || key}</div>
                              <div className="pie-legend-value">{value} ({percentage}%)</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
          </section>

          <section className="report-section">
            <h2 className="section-title">Flotten-KPIs</h2>
            <div className="kpi-grid">
              <div className="kpi-card">
                <div className="kpi-label">Eigene Fahrzeuge</div>
                <div className="kpi-value">
                  {fleet.filter(v => !v.status || v.status === 'owned').length}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Gesamter Ankaufswert</div>
                <div className="kpi-value">
                  {fleet.filter(v => !v.status || v.status === 'owned').reduce((sum, v) => sum + (v.purchasePrice || 0), 0).toFixed(2)} {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Gesamter Marktwert</div>
                <div className="kpi-value">
                  {fleet.filter(v => !v.status || v.status === 'owned').reduce((sum, v) => sum + (v.marketValue || 0), 0).toFixed(2)} {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Verkaufte Fahrzeuge</div>
                <div className="kpi-value">
                  {fleet.filter(v => v.status === 'sold').length}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Gesamter Verkaufswert</div>
                <div className="kpi-value">
                  {fleet.filter(v => v.status === 'sold').reduce((sum, v) => sum + (v.salePrice || 0), 0).toFixed(2)} {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Durchschnittlicher Ankaufspreis</div>
                <div className="kpi-value">
                  {(() => {
                    const owned = fleet.filter(v => !v.status || v.status === 'owned');
                    if (owned.length === 0) return '0.00';
                    const avg = owned.reduce((sum, v) => sum + (v.purchasePrice || 0), 0) / owned.length;
                    return avg.toFixed(2);
                  })()} {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Durchschnittlicher Marktwert</div>
                <div className="kpi-value">
                  {(() => {
                    const owned = fleet.filter(v => !v.status || v.status === 'owned');
                    if (owned.length === 0) return '0.00';
                    const avg = owned.reduce((sum, v) => sum + (v.marketValue || 0), 0) / owned.length;
                    return avg.toFixed(2);
                  })()} {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Gewinn/Verlust (Verkäufe)</div>
                <div className="kpi-value">
                  {(() => {
                    const sold = fleet.filter(v => v.status === 'sold');
                    const profit = sold.reduce((sum, v) => {
                      const salePrice = v.salePrice || 0;
                      const purchasePrice = v.purchasePrice || 0;
                      return sum + (salePrice - purchasePrice);
                    }, 0);
                    return `${profit >= 0 ? '+' : ''}${profit.toFixed(2)}`;
                  })()} {currency}
                </div>
              </div>
            </div>
          </section>

          <section className="report-section">
            <h2 className="section-title">Lager-KPIs</h2>
            <div className="kpi-grid">
              <div className="kpi-card">
                <div className="kpi-label">Gesamt Artikel</div>
                <div className="kpi-value">{inventory.length}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Niedriger Bestand</div>
                <div className="kpi-value">
                  {inventory.filter(item => {
                    const minStock = item.minStock;
                    if (minStock === null || minStock === undefined) return false;
                    return (item.quantity || 0) < minStock;
                  }).length}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Gesamtwert Lager</div>
                <div className="kpi-value">
                  {inventory.reduce((sum, item) => {
                    const price = item.purchasePrice || item.price || 0;
                    const quantity = item.quantity || 0;
                    return sum + (price * quantity);
                  }, 0).toFixed(2)} {currency}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Anschaffungen ({selectedYear})</div>
                <div className="kpi-value">
                  {purchases.filter(p => {
                    if (!p.date) return false;
                    const purchaseDate = new Date(p.date);
                    return purchaseDate.getFullYear() === selectedYear;
                  }).length}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Reports;

